import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useUpdateUser } from '@/api/auth/queries';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useUser } from '@/providers/user-provider';

const updateNameSchema = z.object({
  name: z.string().min(1, 'Name is required').max(50, 'Name must be less than 50 characters'),
});

type UpdateNameForm = z.infer<typeof updateNameSchema>;

export function UpdateNameForm() {
  const { session } = useUser();
  const updateUserMutation = useUpdateUser();

  const form = useForm<UpdateNameForm>({
    resolver: zodResolver(updateNameSchema),
    defaultValues: {
      name: session?.user?.name || '',
    },
  });

  const onSubmit = async (data: UpdateNameForm) => {
    try {
      await updateUserMutation.mutateAsync({
        name: data.name,
      });
    } catch {
      // Error handled by the mutation hook
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile Information</CardTitle>
        <CardDescription>Update your profile information</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={updateUserMutation.isPending || !form.formState.isDirty}
            >
              {updateUserMutation.isPending ? 'Updating...' : 'Update Profile'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
